import React from "react";
import { DonutWithLegend, StatCard } from "@/components/ui";
import { fmt } from "@/lib/formatters";
import { Maximize2, Minimize2, ShoppingCart, ShoppingBag, Users, Package, DollarSign, TrendingUp, History, FileText, Clock, Truck, CheckCircle, RotateCcw, Box, IndianRupee } from "lucide-react";
import { useInventoryStats } from "@/hooks/useGlobalStats";
import { STATUS_VARIANT_MAP } from "@/constants/globalConstants";

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#6366F1', '#EC4899', '#8B5CF6'];

export const InventoryStats = ({
  activeTab,
  viewMode = "inventory",
  subTab = "items",
  isExpanded,
  setIsExpanded,
  inventory = [],
  sales = [],
  returns = [],
  requests = [],
  purchaseReturns = [],
  purchases = [],
  openingStocks = []
}) => {
  const stats = useInventoryStats({
    inventory,
    sales,
    returns,
    requests,
    purchaseReturns,
    purchases,
    openingStocks,
    activeTab
  });

  const { stock, sales: salesStats, requests: requestStats, returns: returnStats } = stats;

  const renderStatsContent = () => {
    // 1. Returns View Mode
    if (viewMode === "returns") {
      const typeLabel = subTab === "master" ? "Master" : 
                        subTab === "purchases" ? "Purchases" : 
                        subTab === "site" ? "Site" : "Damaged";
      
      const typeFiltered = subTab === "master" ? returns :
                           subTab === "purchases" ? returns.filter(r => r.rawType === 'purchase' || r.returnType === 'Purchase') :
                           subTab === "site" ? returns.filter(r => r.rawType === 'site' || r.returnType === 'Site') :
                           returns.filter(r => {
                             if (r.rawType === 'damage' || r.returnType === 'Damage') return true;
                             if (r.originalData?.damageReport?.length > 0) return true;
                             if (r.originalData?.items?.some(it => it.damagedQty > 0)) return true;
                             if (r.items?.some(it => it.damagedQty > 0)) return true;
                             return false;
                           });

      const currentReturns = subTab === "damaged"
        ? typeFiltered
        : typeFiltered.filter(r => ['received', 'restocked'].includes((r.status || '').toLowerCase()));

      const totalItemsCount = subTab === "damaged"
        ? currentReturns.reduce((sum, r) => {
            const orig = r.originalData || r;
            const damageReport = orig.damageReport || [];
            if (damageReport.length > 0) {
               return sum + damageReport.reduce((s, d) => s + (Number(d.damagedQty) || 0), 0);
            }
            const items = orig.items || r.items || [];
            return sum + items.reduce((s, it) => s + (Number(it.damagedQty) || 0), 0);
          }, 0)
        : currentReturns.reduce((sum, r) => sum + (r.items?.reduce((is, it) => is + (Number(it.quantity || it.qty || 0)), 0) || 0), 0);

      const totalAmountVal = subTab === "damaged"
        ? currentReturns.reduce((sum, r) => {
            const orig = r.originalData || r;
            const damageReport = orig.damageReport || [];
            const allItems = orig.items || r.items || [];

            if (damageReport.length > 0) {
              const drTotal = damageReport.reduce((s, d) => {
                const qty = Number(d.damagedQty) || 0;
                let rate = Number(d.rate || 0);
                if (!rate) {
                  const targetId = (d.item?._id || d.item || d.inventoryId?._id || d.inventoryId || '').toString();
                  const matched = allItems.find(it => {
                    const itId = (it.inventoryId?._id || it.inventoryId || it.item?._id || it.item || '').toString();
                    return itId && itId === targetId;
                  }) || allItems[0];
                  rate = Number(matched?.rate || matched?.inventoryId?.purchaseRate || matched?.inventoryId?.unitPrice || matched?.item?.unitPrice || matched?.unitPrice || 0);
                }
                return s + (qty * rate);
              }, 0);
              return sum + (drTotal || Number(r.totalAmount || r.refundAmount || r.amount || 0));
            }

            const itemsTotal = allItems.reduce((s, it) => {
              const qty = Number(it.damagedQty) || 0;
              const rate = Number(it.rate || it.inventoryId?.purchaseRate || it.inventoryId?.unitPrice || it.unitPrice || 0);
              return s + (qty * rate);
            }, 0);
            return sum + (itemsTotal || Number(r.totalAmount || r.refundAmount || r.amount || 0));
          }, 0)
        : currentReturns.reduce((sum, r) => sum + (Number(r.totalAmount || r.refundAmount || r.amount || 0)), 0);

      return (
        <>
          <StatCard
            variant="luxury"
            title="Total Returns"
            value={currentReturns.length}
            topColor="border-t-[#C91D25]"
            iconHoverColor="group-hover:text-[#ffa000]"
            isExpanded={false}
            icon={<RotateCcw />}
          />
          <StatCard
            variant="luxury"
            title="Return Type"
            value={typeLabel}
            topColor="border-t-[#C91D25]"
            iconHoverColor="group-hover:text-[#ffa000]"
            isExpanded={false}
            icon={<History />}
          />
          <StatCard
            variant="luxury"
            title="Return Items"
            value={totalItemsCount}
            topColor="border-t-[#C91D25]"
            iconHoverColor="group-hover:text-[#ffa000]"
            isExpanded={false}
            icon={<Package />}
          />
          <StatCard
            variant="luxury"
            title="Total Amount"
            value={`₹${fmt(totalAmountVal)}`}
            topColor="border-t-[#C91D25]"
            iconHoverColor="group-hover:text-[#ffa000]"
            isExpanded={false}
            icon={<DollarSign />}
          />
        </>
      );
    }

    // 2. Requests View Mode
    if (viewMode === "requests") {
      return (
        <>
          <StatCard
            variant="luxury"
            title="Total Requests"
            value={requestStats.total}
            topColor="border-t-[#C91D25]"
            iconHoverColor="group-hover:text-[#ffa000]"
            isExpanded={isExpanded}
            icon={<FileText />}
          />
          <StatCard
            variant="luxury"
            title="Pending Approval"
            value={requestStats.pending}
            topColor="border-t-[#C91D25]"
            iconHoverColor="group-hover:text-[#ffa000]"
            isExpanded={isExpanded}
            icon={<Clock />}
          />
          <StatCard
            variant="luxury"
            title="In Transit"
            value={requestStats.moving}
            topColor="border-t-[#C91D25]"
            iconHoverColor="group-hover:text-[#ffa000]"
            isExpanded={isExpanded}
            icon={<Truck />}
          />
          <StatCard
            variant="luxury"
            title="Delivered"
            value={requestStats.completed}
            topColor="border-t-[#C91D25]"
            iconHoverColor="group-hover:text-[#ffa000]"
            isExpanded={isExpanded}
            icon={<CheckCircle />}
          />
        </>
      );
    }

    // 3. Inventory Sub-Tabs
    switch (subTab) {
      case "purchases":
        const { purchases: pStats } = stats;
        return (
          <>
            <StatCard
              variant="luxury"
              title="Total Spend"
              value={`₹${fmt(pStats.grossSpend || pStats.totalSpend)}`}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              isExpanded={isExpanded}
              icon={<DollarSign />}
            >
              <DonutWithLegend data={stock.distribution} label="Supplier Distribution" labelColor="text-blue-600" />
            </StatCard>
            <StatCard
              variant="luxury"
              title="Items Purchased"
              value={pStats.grossQty || pStats.totalQty}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              isExpanded={isExpanded}
              icon={<Package />}
            />
            <StatCard
              variant="luxury"
              title="Transactions"
              value={pStats.transactions}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              isExpanded={isExpanded}
              icon={<History />}
            />
            <StatCard
              variant="luxury"
              title="Suppliers"
              value={pStats.suppliersCount}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              isExpanded={isExpanded}
              icon={<Users />}
            />
          </>
        );

      case "sales":
        return (
          <>
            <StatCard
              variant="luxury"
              title="Total Revenue"
              value={`₹${fmt(salesStats.totalRevenue)}`}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              isExpanded={isExpanded}
              icon={<TrendingUp />}
            >
              <DonutWithLegend data={stock.distribution} label="Vendor Distribution" labelColor="text-emerald-600" />
            </StatCard>
            <StatCard
              variant="luxury"
              title="Items Sold"
              value={salesStats.totalQty}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              isExpanded={isExpanded}
              icon={<ShoppingCart />}
            />
            <StatCard
              variant="luxury"
              title="Orders"
              value={salesStats.count}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              isExpanded={isExpanded}
              icon={<ShoppingBag />}
            />
            <StatCard
              variant="luxury"
              title="Total Customers"
              value={salesStats.customersCount}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              isExpanded={isExpanded}
              icon={<Users />}
            />
          </>
        );

      case "open_stock":
        const { openStock } = stats;
        return (
          <>
            <StatCard
              variant="luxury"
              title="Opening Value"
              value={`₹${fmt(openStock.totalAmount)}`}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              isExpanded={isExpanded}
              icon={<DollarSign />}
            >
              <DonutWithLegend data={stock.distribution} label="Category Value Distribution" labelColor="text-blue-600" />
            </StatCard>
            <StatCard
              variant="luxury"
              title="Total Items Qty"
              value={openStock.totalUnits}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              isExpanded={isExpanded}
              icon={<Package />}
            />
            <StatCard
              variant="luxury"
              title="Stock Entries"
              value={openStock.entriesCount}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              isExpanded={isExpanded}
              icon={<History />}
            />
            <StatCard
              variant="luxury"
              title="Categories"
              value={openStock.categoriesCount}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              isExpanded={isExpanded}
              icon={<Users />}
            />
          </>
        );

      case "items":
      default:
        return (
          <>
            <StatCard
              variant="luxury"
              title="TOTAL ITEMS"
              value={stock.totalItems}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              icon={<Box size={20} />}
            />
            <StatCard
              variant="luxury"
              title="INVENTORY VALUE"
              value={`₹${fmt(stock.totalValue)}`}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              icon={<IndianRupee />}
            />
            <StatCard
              variant="luxury"
              title="LOW STOCK"
              value={stock.lowStockCount}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              icon={<Clock size={20} />}
            />
            <StatCard
              variant="luxury"
              title="OUT OF STOCK"
              value={stock.noStockCount}
              topColor="border-t-[#C91D25]"
              iconHoverColor="group-hover:text-[#ffa000]"
              icon={<Package size={20} />}
            />
          </>
        );
    }
  };

  return (
    <div className="space-y-4 relative group/stats">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr transition-all duration-300">
        {renderStatsContent()}
      </div>
    </div>
  );
};
